import type { Theme } from '@mui/material'
import { Box, LinearProgress, linearProgressClasses, styled, TablePagination, Typography } from '@mui/material'
import Paper from 'components/@extended/Paper'
import dayjs from 'dayjs'
import React, { type FC, useState } from 'react'
import { startTransition } from 'react'
import { useNavigate } from 'react-router-dom'
import { ThemeMode } from 'types/config'
import type { UserPeriodOverview } from 'types/dashboard'
import type { Period } from 'types/periods'

interface TeamEmployeeOverviewsProps {
  userPeriodOverviews: UserPeriodOverview[]
  theme: Theme
  view: 'period' | 'annual'
  selectedPeriod?: string
  periods?: Period[]
}

const TeamEmployeeOverviews: FC<TeamEmployeeOverviewsProps> = ({
  userPeriodOverviews,
  theme,
  view,
  selectedPeriod = '',
  periods = [],
}) => {
  const navigate = useNavigate()
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState(10)

  console.log(theme.palette.grey)

  const hoverColor = theme.palette.mode === ThemeMode.DARK ? theme.palette.action.hover : theme.palette.action.hover

  const baseBarColor = theme.palette.mode === ThemeMode.DARK ? theme.palette.info : theme.palette.primary
  const barColor = theme.palette.mode === ThemeMode.DARK ? 'primary' : 'primary'
  const barBackgroundColor = theme.palette.mode === ThemeMode.DARK ? baseBarColor.darker : baseBarColor.lighter
  const barBackgroundColor2 = theme.palette.mode === ThemeMode.DARK ? baseBarColor.light : baseBarColor.darker

  const OverTargetLinearProgress = styled(LinearProgress)({
    backgroundColor: barBackgroundColor,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: barBackgroundColor2,
    },
  })

  const DefaultLinearProgress = styled(LinearProgress)({
    backgroundColor: barBackgroundColor,
    [`&.${linearProgressClasses.colorPrimary}`]: {
      backgroundColor: barBackgroundColor,
    },
  })

  // Process user data based on view type
  const processedUserData = React.useMemo(() => {
    if (!userPeriodOverviews) return []

    // Helper function to find a period by ID
    const findPeriodById = (periodId: string): Period | undefined => {
      const findInPeriods = (periodsToSearch: Period[]): Period | undefined => {
        for (const period of periodsToSearch) {
          if (period.id === periodId) return period
          if (period.children && period.children.length > 0) {
            const found = findInPeriods(period.children)
            if (found) return found
          }
        }
        return undefined
      }
      return findInPeriods(periods)
    }

    // Helper function to calculate proration ratio for a plan within a period
    const calculateProration = (overview: UserPeriodOverview): number => {
      const period = findPeriodById(overview.periodId)
      if (!period) return overview.periodProration // Fallback to period proration if period not found

      const periodStart = dayjs.utc(period.startDate)
      const periodEnd = dayjs.utc(period.endDate)
      const totalPeriodDays = periodEnd.diff(periodStart, 'day') + 1

      // For backward compatibility: use period dates if plan dates are missing
      const planStartDate = overview.planStartDate ? dayjs.utc(overview.planStartDate) : periodStart
      const planEndDate = overview.planEndDate ? dayjs.utc(overview.planEndDate) : periodEnd

      // Calculate the overlap between plan and period
      const effectiveStart = planStartDate.isAfter(periodStart) ? planStartDate : periodStart
      const effectiveEnd = planEndDate.isBefore(periodEnd) ? planEndDate : periodEnd

      // If there's no overlap, return 0
      if (effectiveEnd.isBefore(effectiveStart)) {
        return 0
      }

      // Calculate how many days the plan was valid in the period
      const planValidDays = effectiveEnd.diff(effectiveStart, 'day') + 1

      // Calculate proration: (planValidDays / totalPeriodDays) * periodProration
      const dateProration = planValidDays / totalPeriodDays

      // Return combined proration ratio
      return dateProration * overview.periodProration
    }

    // Helper function to consolidate multiple plans for the same user
    const consolidateUserPlans = (overviews: UserPeriodOverview[]): UserPeriodOverview[] => {
      const consolidatedUsers = new Map<string, UserPeriodOverview>()

      overviews.forEach((overview) => {
        if (overview.planItems.length === 0) return

        // Calculate proration for this plan (includes both date-based and periodProration)
        const prorationRatio = calculateProration(overview)

        const existingUser = consolidatedUsers.get(overview.userId)
        if (existingUser) {
          // Sum up payout amounts and prorated on-target amounts from all plans
          const combinedPlanItems = existingUser.planItems.map((existingItem, index) => {
            const matchingItem = overview.planItems[index]
            if (matchingItem) {
              const combinedPayoutAmount = existingItem.payoutAmount + matchingItem.payoutAmount
              // Prorate the on-target amount: onTargetAmount * periodProration * (planDays/periodDays)
              const proratedOnTargetAmount = matchingItem.onTargetAmount * prorationRatio
              const combinedOnTargetAmount = existingItem.onTargetAmount + proratedOnTargetAmount

              // Recalculate progress percentage based on combined amounts
              const combinedProgress =
                combinedOnTargetAmount > 0 ? (combinedPayoutAmount / combinedOnTargetAmount) * 100 : 0

              return {
                ...existingItem,
                payoutAmount: combinedPayoutAmount,
                onTargetAmount: combinedOnTargetAmount,
                progress: combinedProgress,
              }
            }
            return existingItem
          })

          // Update with combined values
          consolidatedUsers.set(overview.userId, {
            ...existingUser,
            planItems: combinedPlanItems,
          })
        } else {
          // First record for this user - apply proration to on-target amounts
          const proratedPlanItems = overview.planItems.map((item) => ({
            ...item,
            onTargetAmount: item.onTargetAmount * prorationRatio,
          }))

          consolidatedUsers.set(overview.userId, {
            ...overview,
            planItems: proratedPlanItems,
          })
        }
      })

      return Array.from(consolidatedUsers.values())
    }

    if (view === 'period') {
      if (!selectedPeriod) return []

      // If the selected period is a root period, show all data
      const isRootPeriod = periods.some((p) => p.id === selectedPeriod)
      const filteredOverviews = isRootPeriod
        ? userPeriodOverviews.filter((overview) => overview.planItems.length > 0)
        : userPeriodOverviews.filter(
            (overview) => overview.periodId === selectedPeriod && overview.planItems.length > 0
          )
      return consolidateUserPlans(filteredOverviews)
    } else if (view === 'annual') {
      // Annual Progress: Sum payout from all periods (across different period IDs)
      const allValidOverviews = userPeriodOverviews.filter((overview) => overview.planItems.length > 0)
      return consolidateUserPlans(allValidOverviews)
    } else {
      // Default: consolidate all users with plan items
      const allValidOverviews = userPeriodOverviews.filter((overview) => overview.planItems.length > 0)
      return consolidateUserPlans(allValidOverviews)
    }
  }, [userPeriodOverviews, view, selectedPeriod, periods])

  // Reset page when data changes
  React.useEffect(() => {
    setPage(0)
  }, [processedUserData.length])

  const paginatedData = processedUserData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)

  return (
    <Paper sx={{ pl: { xs: 2, sm: 4 }, pr: { xs: 2, sm: 4 }, pt: 2, pb: 2, mt: 2 }}>
      <Typography variant="h4" sx={{ p: 2, fontSize: { xs: '1.5rem', sm: '2rem' } }}>
        Employee Overviews
      </Typography>
      {processedUserData.length === 0 ? (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1" color="text.secondary">
            No employee overviews to display
          </Typography>
        </Box>
      ) : (
        paginatedData.map((employee) => {
          // For period view, we've already filtered at the user level
          // For annual view, use all plan items since they're consolidated
          const filteredPlanItems = employee.planItems

          // OnTargetAmount is already prorated during consolidation
          const totalOnTargetAmount = filteredPlanItems.reduce((sum, item) => sum + item.onTargetAmount, 0)
          const totalPayoutAmount = filteredPlanItems.reduce((sum, item) => sum + item.payoutAmount, 0)

          // Handle zero dollar plans - avoid NaN%
          const isZeroDollarPlan = totalOnTargetAmount === 0
          const progressPercentage = isZeroDollarPlan ? 0 : (totalPayoutAmount / totalOnTargetAmount) * 100

          const overTarget = (100 / progressPercentage) * 100
          const linearProgress = isZeroDollarPlan ? (
            <DefaultLinearProgress
              color={barColor}
              variant="determinate"
              value={0}
              sx={{ height: 12, borderRadius: 5 }}
            />
          ) : progressPercentage > 100 ? (
            <OverTargetLinearProgress
              color={barColor}
              variant="determinate"
              value={overTarget}
              sx={{ height: 12, borderRadius: 5 }}
            />
          ) : (
            <DefaultLinearProgress
              color={barColor}
              variant="determinate"
              value={progressPercentage}
              sx={{ height: 12, borderRadius: 5 }}
            />
          )

          return (
            <Box
              key={`${employee.userId}-${employee.periodId}`}
              sx={{
                p: { xs: 1.5, sm: 2 },
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: hoverColor,
                },
              }}
              onClick={() => startTransition(() => navigate(`/employee/${employee.userId}/compplan`))}
            >
              <Typography variant="subtitle1" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                {employee.firstName} {employee.lastName} - {employee.organizationName}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Box sx={{ width: '100%', mr: 1 }}>{linearProgress}</Box>
              </Box>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: { xs: 'wrap', sm: 'nowrap' },
                  gap: { xs: 1, sm: 0 },
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' }, flex: { xs: '1 1 100%', sm: '1 1 auto' } }}
                >
                  Progress: ${totalPayoutAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })} / On-Target: $
                  {totalOnTargetAmount.toLocaleString('en-US', { maximumFractionDigits: 0 })}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mr: { xs: 0, sm: 2 }, fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                >
                  {isZeroDollarPlan ? '-' : `${progressPercentage.toFixed(0)}%`}
                </Typography>
              </Box>
            </Box>
          )
        })
      )}
      {processedUserData.length > rowsPerPage && (
        <TablePagination
          component="div"
          count={processedUserData.length}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10))
            setPage(0)
          }}
          rowsPerPageOptions={[10, 25, 50]}
        />
      )}
    </Paper>
  )
}

export default TeamEmployeeOverviews
